/* eslint-disable enforce-catch-error-handled/require-catch-error-handling */
// TODO: Remove this eslint-disable comment in a separate PR (RCP-27576)
import {
  CreateIssueRequestDto,
  CreateIssueResponseDto,
  IssueSourceDto,
  SaveActionRequestDto,
} from '@issuemanager/api-client';
import {
  ControlledCKEditor,
  ControlledDateField,
  ControlledFileUploader,
  ControlledTextField,
  ExtendedControlledAutocomplete,
  ExtendedTypography,
  GovmapGroupsSelector,
  UserSelector,
} from '@issuemanager/apps/client/components';
import { useAttachmentsContext } from '@issuemanager/apps/client/contexts';
import {
  useAllMutations,
  useAllQueries,
  useBadRequestOrConflictError,
  useMonitorCreateIssueFormIssueSubtypesLoad,
  useMonitorCreateIssueFormSubmit,
} from '@issuemanager/apps/client/hooks';
import {
  DEBOUNCE_TIME,
  formHasErrorsToDisplay,
  getFormStateErrors,
  joinFormErrorsWithServiceErrors,
  mapFormLabelsToFieldLabel,
} from '@issuemanager/apps/client/utils';
import { useInfiniteIssueSubtypes } from '@issuemanager/client/features/issue-subtypes';
import { useIssueTemplateByIssueTypeAndIssueSubtype } from '@issuemanager/client/features/issue-templates';
import {
  useCreateIssue,
  useIssueSeverities,
  useLocalizedClassification,
  useLocalizedIssueTypes,
} from '@issuemanager/client/features/issues';
import {
  useIssueTypeAndSubtypeSlas,
  useSLAHelperText,
} from '@issuemanager/client/features/slas';
import {
  ISSUE_SUBTYPE_NONE_VALUE,
  ISSUE_TITLE_LENGTH_MAX,
  IssueClassificationEnum,
  IssueType,
  IssueTypeEnum,
} from '@issuemanager/constants';
import {
  AutocompleteInputChangeReason,
  Checkbox,
  FormControlLabel,
  Link,
  Stack,
  Typography,
  debounce,
  useEventCallback,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import {
  getSLAMaxDateTime,
  isDateEarlierThanSLA,
} from '../utils/sla-validation';
import { splitFormData } from '../utils/split-issue-form-data';
import { useTemplateApplication } from './use-template-application';

type LocalFields =
  | 'title'
  | 'issueType'
  | 'issueSubtype'
  | 'description'
  | 'impact'
  | 'attachments'
  | 'source'
  | 'govmapGroups'
  | 'dueDate'
  | 'ownerId'
  | 'severity'
  | 'classification'
  | 'rootCause'
  | 'recommendations'
  | 'includeInAuditReport';

export type UseCreateIssueFormProps = {
  onCreated?: (values: CreateIssueResponseDto) => void;
  onError?: (error: unknown) => void;
  answerIds?: string[];
  defaultValues?: Partial<Omit<CreateIssueRequestDto, 'answerIds'>>;
  sourceInfo?: IssueSourceDto;
  issueType?: IssueType;
  open?: boolean;
  isSourceError?: boolean;
  isSourceExpected?: boolean;
  readOnly?: boolean;
  existingActions?: SaveActionRequestDto[];
};

type UseCreateIssueFormReturnType = {
  form: ReturnType<typeof useForm<CreateIssueRequestDto>>;
  issueTypeSubtypeQueryIsLoading: boolean;
  isTemplateLoading: boolean;
  createIssueIsLoading: boolean;
  createIssueIsSuccess: boolean;
  fields: Record<LocalFields, ReactNode>;
  errors: ReturnType<typeof joinFormErrorsWithServiceErrors>;
  hasErrors: boolean;
  handleCreate: () => void;
  handleSave: (saveIssueMutation: {
    mutateAsync: (
      values: CreateIssueRequestDto,
    ) => Promise<CreateIssueResponseDto>;
  }) => void;
  cleanErrors: () => void;
  cleanForm: () => void;
  shouldShowExtendedFields: boolean;
  showControlDeficiencyExtraFields?: boolean;
  showAuditFindingExtraFields?: boolean;
};

const normalizeSlaIssueSubtype = (
  selectedIssueSubtype: string | undefined,
): string | undefined => {
  if (!selectedIssueSubtype) return undefined;
  if (selectedIssueSubtype === ISSUE_SUBTYPE_NONE_VALUE) return 'null';
  return selectedIssueSubtype;
};

export const useCreateIssueForm = (
  props: UseCreateIssueFormProps,
): UseCreateIssueFormReturnType => {
  const {
    open = true,
    onCreated,
    answerIds,
    readOnly = false,
    isSourceError,
    isSourceExpected,
    onError,
    sourceInfo,
    issueType,
    existingActions,
  } = props;
  const { enqueueSnackbar } = useSnackbar();
  const { isLoading: isAttachmentLoading } = useAttachmentsContext();

  const { t } = useTranslation(['issue', 'common']);
  const { errors: serviceErrors, cleanErrors: cleanServiceErrors } =
    useBadRequestOrConflictError();

  const isEditingExistingIssue = !!props.defaultValues?.issueType;

  const createIssue = useCreateIssue({
    onSuccess: (values) => {
      enqueueSnackbar('', {
        variant: 'success',
        description: (
          <Trans
            t={t}
            i18nKey='form.tooltip.success'
            components={{ b: <b /> }}
            values={{ issueCode: values.code }}
          />
        ),
      });
    },
  });

  const defaultValues = useMemo(
    () => ({
      title: '',
      description: '',
      impact: '',
      attachments: [],
      issueType: undefined,
      issueSubtypeId: '',
      rootCause: '',
      recommendations: '',
      includeInAuditReport: true,
      ...props.defaultValues,
    }),
    [props.defaultValues],
  );

  const form = useForm<CreateIssueRequestDto>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues,
  });

  const selectedIssueType = form.watch('issueType');
  const selectedIssueSubtype = form.watch('issueSubtypeId');
  const selectedSeverity = form.watch('severity');

  const issueTypeAndIssueSubtypeSelected =
    Boolean(selectedIssueType) &&
    selectedIssueSubtype !== undefined &&
    selectedIssueSubtype !== '';

  const {
    data: templateData,
    isFetching: isTemplateFetching,
    dataUpdatedAt,
  } = useIssueTemplateByIssueTypeAndIssueSubtype({
    issueType: selectedIssueType,
    issueSubtypeId:
      selectedIssueSubtype === ISSUE_SUBTYPE_NONE_VALUE
        ? undefined
        : selectedIssueSubtype,
    enabled: issueTypeAndIssueSubtypeSelected && !isEditingExistingIssue,
  });

  const { resetTemplateState } = useTemplateApplication({
    form,
    templateData: isEditingExistingIssue ? undefined : templateData,
    dataUpdatedAt,
    issueTypeAndIssueSubtypeSelected:
      issueTypeAndIssueSubtypeSelected && !isEditingExistingIssue,
  });

  const [subtypeFilterByText, setSubtypeFilterByText] = useState<string>('');

  const issueSubtypesQuery = useInfiniteIssueSubtypes({
    params: {
      filterByName: subtypeFilterByText,
      filterByEnabled: true,
      filterByIssueTypes: selectedIssueType ? [selectedIssueType] : [],
    },
    enabled: !!selectedIssueType,
    handleError: true,
  });

  const { data: SLAs } = useIssueTypeAndSubtypeSlas({
    issueType: selectedIssueType,
    issueSubtype: normalizeSlaIssueSubtype(selectedIssueSubtype),
  });

  const slaHelperText = useSLAHelperText({
    SLAs,
    severity: selectedSeverity,
  });

  const slaMaxDateTime = useMemo(
    () => getSLAMaxDateTime(SLAs, selectedSeverity),
    [SLAs, selectedSeverity],
  );

  const subtypesOptions =
    issueSubtypesQuery.data?.pages.map((c) => c.data).flat() ?? [];

  const handleInputChange = useCallback(
    debounce(
      (
        value: string,
        _field: 'issueType' | 'issueSubtype',
        reason: AutocompleteInputChangeReason,
      ) => {
        if (reason === 'reset') return;
        setSubtypeFilterByText(value);
      },
      DEBOUNCE_TIME,
    ),
    [],
  );

  const issueTypeSubtypeQueryIsLoading = issueSubtypesQuery.isFetching;

  const localizedIssueTypes = useLocalizedIssueTypes();
  const severityValues = useIssueSeverities(false).map((severity) => ({
    label: severity.label,
    value: severity.value as string,
  }));

  const { getClassificationLabel } = useLocalizedClassification();
  const classificationValues = Object.values(IssueClassificationEnum).map(
    (value) => ({
      label: getClassificationLabel(value) ?? value,
      value,
    }),
  );

  const issueTypeEnumOptions = useMemo(
    () =>
      Object.values(IssueTypeEnum).map((enumValue) => ({
        value: enumValue,
        label: localizedIssueTypes[enumValue] ?? enumValue,
      })),
    [localizedIssueTypes],
  );

  // TODO: Replicate this in all form hooks that can potentially be used in a side panel
  // When the form is closed, reset the form to the default values
  useEffect(() => {
    if (open === false) {
      form.reset(defaultValues);
    }
  }, [defaultValues, form, open]);

  useEffect(() => {
    if (!issueType) return;

    form.setValue('issueType', issueType);
  }, [issueType, form]);

  const monitorCreateIssueFormIssueSubtypesLoad =
    useMonitorCreateIssueFormIssueSubtypesLoad();
  const monitorCreateIssueFormSubmit = useMonitorCreateIssueFormSubmit();

  useAllQueries({
    queries: [issueSubtypesQuery],
    onStart: () => {
      monitorCreateIssueFormIssueSubtypesLoad.start();
    },
    onSuccess: () => {
      monitorCreateIssueFormIssueSubtypesLoad.stop();
    },
    onError: () => {
      monitorCreateIssueFormIssueSubtypesLoad.error();
    },
  });

  useAllMutations({
    mutations: [createIssue],
    onStart: () => {
      monitorCreateIssueFormSubmit.start();
    },
    onSuccess: () => {
      monitorCreateIssueFormSubmit.stop();
    },
    onError: () => {
      monitorCreateIssueFormSubmit.error();
    },
  });

  const cleanErrors = (): void => {
    form.clearErrors();
    cleanServiceErrors();
  };

  const handleCreate = useEventCallback(() => {
    setTimeout(async () => {
      if (isAttachmentLoading) {
        enqueueSnackbar(t('issue:form.attachments.uploadInProgress.message'), {
          variant: 'info',
        });
        return;
      }

      const onValidSubmit = async (
        values: CreateIssueRequestDto,
      ): Promise<void> => {
        const { baseValues, typeSpecificValues } = splitFormData(
          values,
          values.issueType,
          'create',
        );

        try {
          const issueResponse = await createIssue.mutateAsync({
            ...baseValues,
            ...typeSpecificValues,
            title: values.title,
            issueType: values.issueType,
            issueSubtypeId:
              values.issueSubtypeId === ISSUE_SUBTYPE_NONE_VALUE
                ? undefined
                : values.issueSubtypeId,
            description: values.description,
            answerIds,
            source: sourceInfo,
          });

          onCreated?.(issueResponse);
        } catch (error) {
          onError?.(error);
          // eslint-disable-next-line no-console
          console.error(error);
        }
      };

      await form.handleSubmit(onValidSubmit)();
    }, 10);
  });

  const handleSave = useEventCallback(
    (saveIssueMutation: {
      mutateAsync: (
        values: CreateIssueRequestDto,
      ) => Promise<CreateIssueResponseDto>;
    }) => {
      setTimeout(async () => {
        if (isAttachmentLoading) {
          enqueueSnackbar(
            t('issue:form.attachments.uploadInProgress.message'),
            { variant: 'info' },
          );
          return;
        }

        const onValidSubmit = async (
          values: CreateIssueRequestDto,
        ): Promise<void> => {
          const { baseValues, typeSpecificValues } = splitFormData(
            values,
            values.issueType,
            'create',
          );

          try {
            await saveIssueMutation.mutateAsync({
              ...baseValues,
              ...typeSpecificValues,
              title: values.title,
              issueType: values.issueType,
              description: values.description,
              answerIds,
              source: sourceInfo,
              ...(existingActions ? { actions: existingActions } : {}),
            });
          } catch (error) {
            onError?.(error);
            // eslint-disable-next-line no-console
            console.error(error);
          }
        };

        await form.handleSubmit(onValidSubmit)();
      }, 10);
    },
  );

  const rules = useMemo(
    () => ({
      title: {
        required: t('issue:form.errorMessages.title.required'),
      },
      issueType: {
        required: t('issue:form.errorMessages.issueType.required'),
      },
      issueSubtype: {
        required: t('issue:form.errorMessages.issueSubtype.required'),
      },
      description: {},
      impact: {},
      severity: {},
      classification: {},
      rootCause: {},
      recommendations: {},
      dueDate: {
        validate: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          earlierThanSLA: (value: any, context: CreateIssueRequestDto) => {
            if (!value || SLAs.length === 0 || !context.severity) {
              return true;
            }
            return (
              isDateEarlierThanSLA(
                SLAs,
                context.severity,
                value as Date,
                undefined,
                true,
              ) || t('issue:form.errorMessages.dueDate.earlierThanSLA')
            );
          },
        },
      },
    }),
    [t, SLAs],
  );

  const labels = {
    issueType: t('issue:form.labels.issueType'),
    subtypeId: t('issue:form.labels.issueSubtype'),
    title: t('issue:form.labels.title'),
    relatedOrgUnit: t('issue:form.labels.relatedOrgUnit'),
    description: t('issue:form.labels.description'),
    impact: t('issue:form.labels.impact'),
    source: t('issue:form.labels.source'),
    attachments: t('issue:form.labels.attachments'),
    dueDate: t('issue:form.labels.dueDate'),
    ownerId: t('issue:form.labels.owner'),
    severity: t('issue:form.labels.severity'),
    classification: t('issue:form.labels.classification'),
    rootCause: t('issue:form.labels.rootCause'),
    recommendations: t('issue:form.labels.recommendations'),
    includeInAuditReport: t('issue:form.labels.includeInAuditReport'),
  };

  const fields: Record<LocalFields, ReactNode> = {
    title: (
      <ControlledTextField
        name='title'
        label={labels.title}
        control={form.control}
        rules={{
          required: t('issue:form.errorMessages.title.required'),
        }}
        maxLength={ISSUE_TITLE_LENGTH_MAX}
        inputProps={{
          readOnly,
        }}
      />
    ),
    issueType: (
      <ControlledTextField
        name='issueType'
        data-testid='categoryId'
        label={labels.issueType}
        control={form.control}
        rules={rules.issueType}
        options={issueTypeEnumOptions}
        onChange={() => {
          if (form.getValues('issueSubtypeId')) {
            form.setValue('issueSubtypeId', ISSUE_SUBTYPE_NONE_VALUE);
          }
        }}
        placeholder={t('common:inputs.listBox.empty')}
        inputProps={{
          readOnly: isEditingExistingIssue || isSourceExpected,
        }}
      />
    ),
    issueSubtype: (
      <ExtendedControlledAutocomplete
        name='issueSubtypeId'
        label={labels.subtypeId}
        data-testid='subcategoryId'
        control={form.control}
        rules={rules.issueSubtype}
        loading={issueSubtypesQuery.isFetching}
        options={[
          { id: ISSUE_SUBTYPE_NONE_VALUE, label: t('common:subtypes.none') },
          ...subtypesOptions.map((issueSubtype) => ({
            id: issueSubtype.id,
            label: issueSubtype.name,
          })),
        ]}
        onTextChange={(
          value: string,
          reason: AutocompleteInputChangeReason,
        ) => {
          handleInputChange(value, 'issueSubtype', reason);
        }}
        onChangeProp={(option) => {
          // When the clear button (X) is clicked, reset the entire form
          if (!option) {
            const currentIssueType = form.getValues('issueType');
            form.reset(defaultValues);
            if (currentIssueType) {
              form.setValue('issueType', currentIssueType);
            }
            // Reset template application tracking
            resetTemplateState();
          }
        }}
        placeholder={t('common:inputs.listBox.empty')}
        readOnly={isEditingExistingIssue}
      />
    ),
    govmapGroups: (
      <Controller
        name='govmapGroups'
        control={form.control}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <GovmapGroupsSelector
            value={value ?? []}
            onChange={onChange}
            label={labels.relatedOrgUnit}
            error={!!error?.message}
            helperText={error?.message}
            readOnly={readOnly}
          />
        )}
      />
    ),
    description: (
      <ControlledCKEditor
        name='description'
        label={labels.description}
        control={form.control}
        rules={rules.description}
        disabled={readOnly}
      />
    ),
    impact: (
      <ControlledCKEditor
        name='impact'
        label={labels.impact}
        control={form.control}
        disabled={readOnly}
      />
    ),
    attachments: !isEditingExistingIssue ? (
      <ControlledFileUploader
        disabled={createIssue.isPending || readOnly}
        control={form.control}
        label={labels.attachments}
        name='attachments'
        permissions={[]}
      />
    ) : null,
    source: (
      <Stack spacing={0.5}>
        {isSourceExpected && (
          <ExtendedTypography variant='textSm'>
            {labels.source}
          </ExtendedTypography>
        )}
        {sourceInfo && (
          <Link href={sourceInfo.link} target='_blank'>
            <Typography fontWeight='bold'>
              {isSourceError
                ? ''
                : `${sourceInfo.name} - ${sourceInfo.entityType} - ${sourceInfo.id}`}
            </Typography>
          </Link>
        )}
      </Stack>
    ),
    dueDate: (
      <ControlledDateField
        name='dueDate'
        label={labels.dueDate}
        labelInfo={slaHelperText}
        control={form.control}
        rules={rules.dueDate}
        disablePast
        maxDateTime={slaMaxDateTime}
        data-testid='dueDate'
        readOnly={readOnly}
      />
    ),
    ownerId: (
      <Controller
        name='ownerId'
        control={form.control}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <UserSelector
            value={value}
            initialValue={value || props.defaultValues?.ownerId}
            onChange={onChange}
            readOnly={readOnly}
            TextFieldProps={{
              label: labels.ownerId,
              'data-testid': 'ownerId',
              error: !!error?.message,
              helperText: error?.message,
            }}
          />
        )}
      />
    ),
    severity: (
      <ControlledTextField
        name='severity'
        label={labels.severity}
        control={form.control}
        rules={rules.severity}
        options={severityValues}
        data-testid='severity'
        inputProps={{
          readOnly,
        }}
      />
    ),
    classification: (
      <ControlledTextField
        name='classification'
        label={labels.classification}
        data-testid='classification'
        control={form.control}
        rules={rules.classification}
        options={classificationValues}
        inputProps={{
          readOnly,
        }}
      />
    ),
    rootCause: (
      <ControlledCKEditor
        name='rootCause'
        label={labels.rootCause}
        control={form.control}
        rules={rules.rootCause}
        testId='rootCause'
        disabled={readOnly}
      />
    ),
    recommendations: (
      <ControlledCKEditor
        name='recommendations'
        label={labels.recommendations}
        control={form.control}
        rules={rules.recommendations}
        testId='recommendations'
        disabled={readOnly}
      />
    ),
    includeInAuditReport: (
      <Controller
        name='includeInAuditReport'
        control={form.control}
        render={({ field: { value, onChange } }) => (
          <FormControlLabel
            control={
              <Checkbox
                onChange={(e) => onChange(e.currentTarget.checked)}
                checked={Boolean(value)}
                data-testid='includeInAuditReport-checkbox'
                disabled={readOnly}
              />
            }
            label={labels.includeInAuditReport}
          />
        )}
      />
    ),
  };

  const formStateErrors = getFormStateErrors<CreateIssueRequestDto>(
    mapFormLabelsToFieldLabel(labels),
    rules,
    form.formState.errors,
  );
  const errors = joinFormErrorsWithServiceErrors(
    formStateErrors,
    serviceErrors,
  );

  const hasErrors = formHasErrorsToDisplay(errors);

  return {
    form,
    issueTypeSubtypeQueryIsLoading,
    isTemplateLoading: isTemplateFetching && !isEditingExistingIssue,
    createIssueIsLoading: createIssue.isPending,
    createIssueIsSuccess: createIssue.isSuccess,
    fields,
    errors,
    hasErrors,
    handleCreate,
    handleSave,
    cleanErrors,
    cleanForm: form.reset,
    shouldShowExtendedFields:
      issueTypeAndIssueSubtypeSelected || isEditingExistingIssue,
    showControlDeficiencyExtraFields:
      selectedIssueType === IssueTypeEnum.CONTROL_DEFICIENCY,
    showAuditFindingExtraFields:
      selectedIssueType === IssueTypeEnum.AUDIT_FINDING,
  };
};
